/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Visible, Hidden } from 'react-grid-system';
import { PageContainer, Anchor, Icon, Size } from '@vaisala/rockhopper-components';
import { VaiColor, VaiIcon } from '@vaisala/rockhopper-design-tokens';
import AppControls from '../components/custom/AppControls';
import ParameterControls from '../components/custom/ParameterControls';
import Footer from '../components/custom/Footer';
import { OptionsConfig } from '../components/custom/OptionsModal';
import MainBanner from '../components/custom/MainBanner';
import CalculationController from '../modules/calculator/CalculationController';
import { CalculatorResult, ParameterModifier, ParameterName, ParameterValueElement } from '../modules/calculator/calculatorTypes';
import UnitController from '../modules/units/UnitController';
import Analytics from '../modules/analytics/Analytics';
import { UnitPreset } from '../modules/units/unitTypes';
import PersistenceController from '../modules/persistence/PersistenceController';
import { isAdvanced } from '../modules/calculator/configParser';
import ProductRecommendations from '../components/custom/ProductRecommendations';
import { Product, recommendProducts, pickTwoRandom } from '../modules/calculator/ProductRecommendationFunctions';
import UnitConverter from '../modules/units/UnitConverter';

interface LandingPageProps {
  onHelpOpen: VoidFunction;
  onOptionsOpen: (title: string, config: OptionsConfig) => void;
  unitPresetIndex: number;
  unitOptions: string[];
  unitIndex: number;
  onChooseUnit: (ind: number) => void;
}

const LandingPage = ({ onHelpOpen, onOptionsOpen, unitPresetIndex, unitOptions, unitIndex, onChooseUnit }: LandingPageProps) => {
  const { t } = useTranslation();
  const [currentResult, setCurrentResult] = React.useState<CalculatorResult | undefined>(undefined);
  const [currentUnitPreset, setCurrentUnitPreset] = React.useState<UnitPreset | undefined>(undefined);
  const [advancedMode, setAdvancedMode] = React.useState(false);
  const [elevationMode, setElevationMode] = React.useState(false);

  const unitConverter = new UnitConverter();

  React.useEffect(() => {
    // read persisted values
    setCurrentResult(CalculationController.begin());
    const lastHum = PersistenceController.retrieve('lastHumidityParameter');
    onSetAdvancedMode(PersistenceController.retrieve('advancedMode'), lastHum);
    onSetElevationMode(PersistenceController.retrieve('elevationMode'));
    onSelectRow(PersistenceController.retrieve('selectedRowIndex'), lastHum);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setCurrentUnitPreset(UnitController.currentPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitPresetIndex, currentUnitPreset]);

  const resetParameters = (log = true) => {
    setSelectedRowIndex(0);
    setCurrentResult(CalculationController.reset(log));
  };

  const [selectedRowIndex, setSelectedRowIndex] = React.useState(0);
  const [lastHumidityParameter, setLastHumidityParameter] = React.useState<ParameterName | undefined>(undefined);
  const onSelectRow = (ind: number, parameterName: ParameterName) => {
    if (CalculationController.isHumidityParameter(parameterName)) {
      setLastHumidityParameter(parameterName);
    }
    setSelectedRowIndex(ind);
    PersistenceController.store('selectedRowIndex', ind);
    PersistenceController.store('lastHumidityParameter', parameterName);
  };

  const onParameterChange = (parameterName: ParameterName, element: ParameterValueElement, value: ParameterModifier) => {
    setCurrentResult(CalculationController.calculate(parameterName, element, value, elevationMode, lastHumidityParameter));
  };

  const [allRecommendedProducts, setAllRecommendedProducts] = React.useState<Product[]>([]);
  const [recommendedProducts, setRecommendedProducts] = React.useState<Product[]>([]);

  React.useEffect(() => {
    if (currentResult) {
      const recommendations = recommendProducts(currentResult?.parameters.relativeHumidity.value, currentResult?.parameters.temperature.value, currentResult?.parameters.pressure.value, allRecommendedProducts);
      setAllRecommendedProducts(recommendations.products);
      if (!recommendations.previous) {
        setRecommendedProducts(pickTwoRandom(recommendations.products));
      }
    }
  },[currentResult]);

  const onSelectUnit = (parameterName: ParameterName, unitIndex: number) => {
    UnitController.chooseManualUnit(parameterName, unitIndex);
    setCurrentUnitPreset({ ...UnitController.currentPreset });
  };

  const onToggleAdvanced = () => {
    onSetAdvancedMode(!advancedMode, lastHumidityParameter);
  };

  const onSetAdvancedMode = (advanced: boolean, _lastHumidityParameter?: ParameterName) => {
    PersistenceController.store('advancedMode', advanced);
    setAdvancedMode(advanced);
    Analytics.log('settings.mode', advanced ? 'advanced' : 'basic');
    if (!advanced) {
      setCurrentResult(CalculationController.resetAdvanced(elevationMode));
      if (!_lastHumidityParameter || isAdvanced(_lastHumidityParameter)) {
        onSelectRow(0, CalculationController.defaultSelectedHumidityParameter);
      }
    }
  };

  const onToggleElevation = (forceElevation: null | boolean | 'LEFT' | 'RIGHT' = null) => {
    if (typeof forceElevation === 'boolean' && forceElevation !== null) {
      onSetElevationMode(forceElevation)
    } else {
      onSetElevationMode(!elevationMode);
    }
  };

  const onSetElevationMode = (elevation: boolean) => {
    PersistenceController.store('elevationMode', elevation);
    setElevationMode(elevation);
    Analytics.log('settings.pressure_mode', elevation ? 'elevation' : 'pressure');
  };

  return (
    <PageContainer.Content>
      <Container xs sm md>
        <Row className={`main-content ${advancedMode ? 'advanced' : 'basic'}`}>
          <Col xs={12} xl={9}>
            <AppControls
              onReset={resetParameters}
              advancedMode={advancedMode}
              onToggleAdvanced={onToggleAdvanced}
              unitOptions={unitOptions}
              unitIndex={unitIndex}
              onChooseUnit={onChooseUnit}
            />
            <ParameterControls
              currentResult={currentResult}
              onParameterChange={onParameterChange}
              advancedMode={advancedMode}
              selectedRowIndex={selectedRowIndex}
              onSelectRow={onSelectRow}
              onSelectUnit={onSelectUnit}
              onHelpOpen={onHelpOpen}
              onOptionsOpen={onOptionsOpen}
              elevationMode={elevationMode}
              onToggleElevation={onToggleElevation}
            />
            {advancedMode && (
              <Hidden xs sm md lg>
                <Anchor
                  className="conversion-formulas-link"
                  to="https://www.vaisala.com/system/files/documents/Humidity-conversion-formulas-Technical-eBook-B210973EN.pdf"
                  openInNewWindow={true}
                >
                  {t('ui.humidity.formulas')} <Icon name={VaiIcon.DocumentPdf} size={Size.S} color={VaiColor.AquaVaisala} />
                </Anchor>
              </Hidden>
            )}
          </Col>
          <Col lg={12} xl={advancedMode ? 3 : 9}>
            <ProductRecommendations variant="desktop" advanced={advancedMode} recommendedProducts={recommendedProducts} />
            <Visible xs sm>
              <MainBanner variant="mobile" />
            </Visible>
            <Visible md>
              <MainBanner variant="tablet" />
            </Visible>
          </Col>
          <Col className="conversion-formulas-link-bottom">
            <Anchor
              className="conversion-formulas-link"
              to="https://www.vaisala.com/system/files/documents/Humidity-conversion-formulas-Technical-eBook-B210973EN.pdf"
              openInNewWindow={true}
            >
              {t('ui.humidity.formulas')} <Icon name={VaiIcon.DocumentPdf} size={Size.S} color={VaiColor.AquaVaisala} />
            </Anchor>
          </Col>
        </Row>

        <Row className="gtm-content" />

        <Row className="footer-row">
          <Footer />
        </Row>
      </Container>
    </PageContainer.Content>
  );
};

export default LandingPage;
